// Comisiones de planner (FRA-355). Un planner no cobra tarifa: cobra
// comisión, casi siempre sobre el valor del evento. Por eso deja de ser
// "un proveedor con costo de servicio" y pasa a tener su ACUERDO —monto
// fijo o porcentaje, lo que se haya negociado con ese planner— con
// historial (patrón de la Fase 0). Al ligarlo a un evento se propone la
// comisión vigente, se puede ajustar para ese evento en concreto, y se
// registra como costo del evento con categoría propia: baja su utilidad,
// porque al final es costo de adquisición.
//
// El acuerdo vive en el doc del proveedor (`comision: {tipo, valor}`) y su
// historial en proveedores/{pid}/comisionHistorial. El costo por evento es
// un SNAPSHOT: cambiar el acuerdo después no re-afecta eventos anteriores.

import { addDoc, collection, doc, serverTimestamp, Timestamp } from "firebase/firestore";
import { db } from "../firebase";
import { fechaDeDia } from "./dates";
import { CATEGORIA_COMISION_PLANNER, esComisionPlanner, costosPorEvento } from "./costos";
import { setPrecioAcordado } from "./precioAcordado";
import { getValorEvento } from "./valorEvento";

export const TIPOS_COMISION = [
  { value: "porcentaje", label: "Porcentaje del valor del evento" },
  { value: "fijo", label: "Monto fijo por evento" },
];

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
const fmt = (n) => "$" + round2(n).toLocaleString("es-MX", { maximumFractionDigits: 2 });

// Acuerdo vigente del planner, o null si no tiene. Lectura tolerante: un
// proveedor sin `comision` simplemente no tiene acuerdo.
export function acuerdoDe(proveedor) {
  const c = proveedor?.comision;
  if (!c || typeof c !== "object") return null;
  const valor = Number(c.valor) || 0;
  if (!(valor > 0)) return null;
  return { tipo: c.tipo === "fijo" ? "fijo" : "porcentaje", valor };
}

export const describeAcuerdo = (acuerdo) => {
  if (!acuerdo) return "Sin acuerdo de comisión";
  return acuerdo.tipo === "fijo"
    ? `${fmt(acuerdo.valor)} fijos por evento`
    : `${acuerdo.valor}% del valor del evento`;
};

// La comisión que se propone al ligar el planner a un evento: 10 % de
// $200,000 son $20,000; un monto fijo es ese monto. Sin acuerdo, cero y
// se captura a mano.
export function comisionPropuesta(proveedor, valorEvento) {
  const acuerdo = acuerdoDe(proveedor);
  const valor = Number(valorEvento) || 0;
  if (!acuerdo) return { monto: 0, descripcion: "Sin acuerdo de comisión: captúrala a mano", acuerdo: null };
  if (acuerdo.tipo === "fijo") {
    return { monto: round2(acuerdo.valor), descripcion: `Monto fijo acordado: ${fmt(acuerdo.valor)}`, acuerdo };
  }
  const monto = round2((valor * acuerdo.valor) / 100);
  return {
    monto,
    descripcion:
      valor > 0
        ? `${acuerdo.valor}% de ${fmt(valor)}`
        : `${acuerdo.valor}% del valor del evento — el evento todavía no tiene valor`,
    acuerdo,
  };
}

// El costo de comisión de un planner en un evento (uno por planner y evento).
export const comisionDeEvento = (costos, eventId, plannerId) =>
  (costos || []).find(
    (c) => c?.eventId === eventId && c?.proveedorId === plannerId && esComisionPlanner(c)
  ) || null;

// Todas las comisiones de un planner, con su evento y estado de pago: lo
// que su ficha lista.
export const comisionesDePlanner = (costos, plannerId) =>
  (costos || []).filter((c) => c?.proveedorId === plannerId && esComisionPlanner(c));

// ── Escrituras ───────────────────────────────────────────────────────

// Cambia el acuerdo del planner y asienta el historial en el mismo batch.
// El registro guarda el `valor` (porcentaje o monto) y el `tipo` aparte,
// para que "10 % → 12 %" y "$15,000 → 10 %" se lean bien en la línea de
// tiempo.
export async function setAcuerdoComision({
  venueId,
  proveedor,
  tipo,
  valor,
  notas = null,
  user,
  userInfo,
}) {
  if (!venueId || !proveedor?.id || !user?.uid) throw new Error("Sin venue, proveedor o usuario");
  const anterior = acuerdoDe(proveedor);
  const v = Number(valor) || 0;
  const nuevo = v > 0 ? { tipo: tipo === "fijo" ? "fijo" : "porcentaje", valor: v } : null;
  if (nuevo?.tipo === "porcentaje" && v > 100) throw new Error("El porcentaje no puede ser mayor a 100.");
  const docRef = doc(db, "venues", venueId, "proveedores", proveedor.id);
  await setPrecioAcordado({
    docRef,
    vigentePatch: { comision: nuevo },
    monto: nuevo ? nuevo.valor : 0,
    montoAnterior: anterior ? anterior.valor : 0,
    notas,
    user,
    userInfo,
    subcoleccion: "comisionHistorial",
    extra: { tipo: nuevo ? nuevo.tipo : null, tipoAnterior: anterior ? anterior.tipo : null },
  });
  return nuevo;
}

// Registra la comisión de un evento como costo con categoría propia
// (`CATEGORIA_COMISION_PLANNER`): el ledger la etiqueta aparte, Por pagar la
// lista mientras esté por pagar, y al pagarse entra a Movimientos con su
// fecha de pago. Devuelve { costoId, monto }.
export async function registrarComisionDeEvento({
  venueId,
  eventId,
  eventData = null,
  proveedor,
  monto,
  pagado = false,
  fechaPago = null,
  metodo = null,
  notas = null,
  userId,
}) {
  if (!venueId || !eventId || !proveedor?.id || !userId) throw new Error("Sin venue, evento, planner o usuario");
  const m = Math.round((Number(monto) || 0) * 100) / 100;
  if (!(m > 0)) throw new Error("La comisión necesita un monto mayor a cero.");
  const acuerdo = acuerdoDe(proveedor);
  const ref = await addDoc(collection(db, "venues", venueId, "costos"), {
    eventId,
    concepto: `Comisión · ${proveedor.nombre || "Planner"}`,
    categoria: CATEGORIA_COMISION_PLANNER,
    proveedorId: proveedor.id,
    proveedorNombre: proveedor.nombre || "",
    conceptoId: null,
    monto: m,
    montoOriginal: m,
    // El día del evento como "día calendario" (mediodía en la zona del
    // venue), no el instante: ver fechaDeDia en utils/dates.js.
    fecha: fechaDeDia(eventData?.startTime) || Timestamp.now(),
    estado: pagado ? "pagado" : "por_pagar",
    fechaPago: pagado ? fechaPago || Timestamp.now() : null,
    origen: "comision_planner",
    comisionAcuerdo: acuerdo, // snapshot del acuerdo con que se calculó
    metodo: pagado ? metodo || null : null,
    notas: notas || null,
    createdAt: serverTimestamp(),
    createdBy: userId,
    updatedAt: null,
    updatedBy: null,
  });
  return { costoId: ref.id, monto: m };
}

// ── Resultados de un planner ─────────────────────────────────────────
// Lo que su ficha necesita para contestar "¿cómo nos ha ido con este
// planner?" antes de aceptarle otro evento: cada evento que trajo (por
// `plannerId`), cuánto valió, cuánto se cobró y qué falta, qué costó —con
// la comisión del planner separada del resto— y qué utilidad dejó. Los
// cancelados se listan sin valor ni utilidad: su gasto fue real, pero no
// hubo venta contra la que medirlo, así que no entran al margen.
export function resumenPlanner({ plannerId, events = [], payments = [], costos = [] }) {
  const vacio = {
    eventos: [], valorTotal: 0, pagado: 0, debe: 0, cancelados: 0,
    costosTotal: 0, comisionTotal: 0, utilidad: 0, margen: null, tasaEfectiva: null,
  };
  if (!plannerId) return vacio;
  const suyos = (events || []).filter(
    (e) => e && e.plannerId === plannerId && (e.eventType || "event") === "event"
  );
  if (suyos.length === 0) return vacio;
  const ids = new Set(suyos.map((e) => e.id));
  const pagosPorEvento = {};
  (payments || []).forEach((p) => {
    if (!ids.has(p?.eventId)) return;
    pagosPorEvento[p.eventId] = (pagosPorEvento[p.eventId] || 0) + (Number(p.monto) || 0);
  });
  const costosDeEventos = costosPorEvento(costos);
  const ms = (v) => {
    if (!v) return 0;
    if (typeof v.toMillis === "function") return v.toMillis();
    if (typeof v.toDate === "function") return v.toDate().getTime();
    if (v instanceof Date) return v.getTime();
    if (typeof v.seconds === "number") return v.seconds * 1000;
    const d = new Date(v);
    return isNaN(d) ? 0 : d.getTime();
  };
  let valorTotal = 0, pagado = 0, cancelados = 0, costosTotal = 0, comisionTotal = 0;
  const eventos = suyos
    .map((e) => {
      const cancelado = e.status === "cancelado";
      const valor = cancelado ? 0 : getValorEvento(e);
      const pag = pagosPorEvento[e.id] || 0;
      const cos = costosDeEventos[e.id]?.total || 0;
      const comisionDoc = comisionDeEvento(costos, e.id, plannerId);
      const comision = Number(comisionDoc?.monto) || 0;
      if (cancelado) cancelados += 1;
      else {
        costosTotal += cos;
        comisionTotal += comision;
      }
      valorTotal += valor;
      pagado += pag;
      return {
        eventId: e.id,
        nombre: e.nombre || "Sin nombre",
        startTime: e.startTime,
        cancelado,
        valor,
        pagado: pag,
        saldo: cancelado ? 0 : Math.max(0, valor - pag),
        // Todos los costos del evento, comisión incluida.
        costos: cos,
        comision,
        comisionEstado: comisionDoc ? comisionDoc.estado || "por_pagar" : null,
        utilidad: cancelado ? null : valor - cos,
        margen: !cancelado && valor > 0 ? (valor - cos) / valor : null,
      };
    })
    .sort((a, b) => ms(b.startTime) - ms(a.startTime));
  const utilidad = valorTotal - costosTotal;
  return {
    eventos,
    valorTotal,
    pagado,
    debe: eventos.reduce((s, e) => s + e.saldo, 0),
    cancelados,
    costosTotal,
    comisionTotal,
    utilidad,
    margen: valorTotal > 0 ? utilidad / valorTotal : null,
    // Comisión efectiva sobre lo que trajo; null sin valor, como en Reportes.
    tasaEfectiva: valorTotal > 0 ? comisionTotal / valorTotal : null,
  };
}
